/**
 * VoiceButton Component
 * 
 * Voice mode toggle button with visual feedback for:
 * - Connecting (pulsing)
 * - Listening (waveform animation)
 * - Speaking (animated bars)
 * - Error states
 * 
 * @author Heseda AI Team
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './VoiceButton.css';

export interface VoiceButtonProps {
  /** Current status of voice session */
  status: 'disconnected' | 'connecting' | 'connected' | 'listening' | 'speaking' | 'error';
  /** Click handler */
  onClick: () => void;
  /** Whether voice is supported */
  isSupported: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Whether button is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Custom className */
  className?: string;
}

/**
 * Microphone button with animated visual feedback
 */
export const VoiceButton: React.FC<VoiceButtonProps> = ({
  status,
  onClick,
  isSupported,
  size = 'medium',
  disabled = false,
  error,
  className = '',
}) => {
  const { t } = useTranslation('chat');

  // Determine if button should be active (any connected state)
  const isActive = ['connected', 'listening', 'speaking'].includes(status);

  // Build button class names
  const buttonClasses = [
    'voice-button',
    `voice-button--${size}`,
    `voice-button--${status}`,
    isActive ? 'voice-button--active' : '',
    disabled || !isSupported ? 'voice-button--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  // Determine tooltip text
  const getTooltip = (): string => {
    if (!isSupported) {
      return t('voice.notSupported', 'Voice not supported in this browser');
    }
    if (error) {
      return error;
    }
    switch (status) {
      case 'connecting':
        return t('voice.connecting', 'Connecting...');
      case 'connected':
        return t('voice.tapToSpeak', 'Voice mode active - speak naturally');
      case 'listening':
        return t('voice.listening', 'Listening...');
      case 'speaking':
        return t('voice.speaking', 'AI is speaking...');
      case 'error':
        return t('voice.error', 'Voice error - tap to retry');
      default:
        return t('voice.startVoice', 'Start voice mode');
    }
  };

  // Render microphone icon
  const renderIcon = () => {
    if (status === 'listening') {
      // Waveform animation when listening
      return (
        <div className="voice-button__waveform">
          <span className="voice-button__wave"></span>
          <span className="voice-button__wave"></span>
          <span className="voice-button__wave"></span>
          <span className="voice-button__wave"></span>
          <span className="voice-button__wave"></span>
        </div>
      );
    }

    if (status === 'speaking') {
      // Speaker animation when AI is speaking
      return (
        <div className="voice-button__speaker">
          <svg viewBox="0 0 24 24" fill="currentColor" className="voice-button__speaker-icon">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
          <div className="voice-button__speaker-waves">
            <span className="voice-button__speaker-wave"></span>
            <span className="voice-button__speaker-wave"></span>
          </div>
        </div>
      );
    }

    // Default microphone icon
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="voice-button__mic-icon">
        <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
      </svg>
    );
  };

  return (
    <button
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || !isSupported}
      title={getTooltip()}
      aria-label={getTooltip()}
      type="button"
    >
      <div className="voice-button__content">
        {renderIcon()}
        
        {/* Pulse ring for connecting state */}
        {status === 'connecting' && (
          <div className="voice-button__pulse-ring"></div>
        )}
        
        {/* Active ring for connected states */}
        {isActive && status !== 'connecting' && (
          <div className="voice-button__active-ring"></div>
        )}
      </div>
      
      {/* Error indicator */}
      {status === 'error' && (
        <div className="voice-button__error-dot"></div>
      )}
    </button>
  );
};

export default VoiceButton;
